import "@/index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";

import Home from "@/pages/Home";
import MarkerCollection from "@/pages/MarkerCollection";
import { useState, useEffect } from "react";
import { http } from "@/lib/api";

/* Conditionally renders MarkerCollection as homepage when promotion mode is on */
function HomeRouter() {
  const [hideArt, setHideArt] = useState(null);
  useEffect(() => {
    http.get("/public/settings").then(r => setHideArt(!!r.data?.hide_art_collection)).catch(() => setHideArt(false));
  }, []);
  if (hideArt === null) return null; // brief loading — prevents flash of wrong page
  return hideArt ? <MarkerCollection /> : <Home />;
}
import Shop from "@/pages/Shop";
import ArtworkDetail from "@/pages/ArtworkDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import MobileGallery from "@/pages/MobileGallery";
import QRPurchase from "@/pages/QRPurchase";
import ProofOfPurchase from "@/pages/ProofOfPurchase";
import Admin from "@/pages/Admin";
import Stories from "@/pages/Stories";
import Subscriptions from "@/pages/Subscriptions";
import FoundingWall from "@/pages/FoundingWall";
import WhatDoYouSeeGallery from "@/pages/WhatDoYouSeeGallery";
import Refer from "@/pages/Refer";
import PartnerApply from "@/pages/PartnerApply";
import Partner from "@/pages/Partner";
import PartnerOffer from "@/pages/PartnerOffer";
import PartnerAgreement from "@/pages/PartnerAgreement";
import PartnerOnboard from "@/pages/PartnerOnboard";
import ApplicationStatus from "@/pages/ApplicationStatus";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CmsPage from "@/pages/CmsPage";
import GiftCards from "@/pages/GiftCards";
import GiftCardView from "@/pages/GiftCardView";
import Registry from "@/pages/Registry";
import TruthPage from "@/pages/TruthPage";
import Support from "@/pages/Support";
import TruthWall from "@/pages/TruthWall";
import TruthMarkers from "@/pages/TruthMarkers";
import MarkerShop from "@/pages/MarkerShop";
import TruthMarkerPage from "@/pages/TruthMarkerPage";
import TruthWallPage from "@/pages/TruthWallPage";
import ClaimTruth from "@/pages/ClaimTruth";
import ClaimEntry from "@/pages/ClaimEntry";
import TruthStand from "@/pages/TruthStand";
import MirrorConversations from "@/pages/MirrorConversations";
import ReflectionLibrary, { ReflectionProductDetail } from "@/pages/ReflectionLibrary";
import BookingPage from "@/pages/BookingPage";
import ClientPortal from "@/pages/ClientPortal";
import EmptyChair from "@/pages/EmptyChair";
import BreathingRoom from "@/pages/BreathingRoom";
import Workshops from "@/pages/Workshops";
import WorkshopDetail from "@/pages/WorkshopDetail";
import GuestRelease from "@/pages/GuestRelease";
import MarkerCollectionDetail from "@/pages/MarkerCollectionDetail";
import CharityImpactDashboard from "@/pages/CharityImpactDashboard";
import ChallengeBoard from "@/pages/ChallengeBoard";
import MCGallery from "@/pages/MCGallery";
import MCOriginals from "@/pages/MCOriginals";
import MCCommissions from "@/pages/MCCommissions";
import MCAbout from "@/pages/MCAbout";
import MCProcess from "@/pages/MCProcess";
import BoothSalesMode from "@/pages/BoothSalesMode";
import TheMarket from "@/pages/TheMarket";
import MarketProductDetail from "@/pages/MarketProductDetail";
import NameWinner from "@/pages/NameWinner";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<HomeRouter />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/artwork/:slug" element={<ArtworkDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/account" element={<Account />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/mobile-gallery" element={<MobileGallery />} />
              <Route path="/qr/:code" element={<QRPurchase />} />
              <Route path="/proof/:orderNumber" element={<ProofOfPurchase />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/stories" element={<Stories />} />
              <Route path="/subscriptions" element={<Subscriptions />} />
              <Route path="/founding-wall" element={<FoundingWall />} />
              <Route path="/what-do-you-see" element={<WhatDoYouSeeGallery />} />
              <Route path="/refer" element={<Refer />} />
              <Route path="/partner-apply" element={<PartnerApply />} />
              <Route path="/partner-apply/status/:token" element={<ApplicationStatus />} />
              <Route path="/partner" element={<Partner />} />
              <Route path="/partner/offer/:id" element={<PartnerOffer />} />
              <Route path="/partner/agreement/:id" element={<PartnerAgreement />} />
              <Route path="/partner/onboard/:token" element={<PartnerOnboard />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/p/:slug" element={<CmsPage />} />
              <Route path="/gift-cards" element={<GiftCards />} />
              <Route path="/gift-cards/thank-you" element={<GiftCardView purchaserMode />} />
              <Route path="/gift-card/:code" element={<GiftCardView />} />
              <Route path="/registry" element={<Registry />} />
              <Route path="/truths/:truth_id" element={<TruthPage />} />
              <Route path="/support" element={<Support />} />
              <Route path="/support/thanks" element={<Support />} />
              <Route path="/truth-wall" element={<TruthWall />} />
              <Route path="/truth-markers" element={<TruthMarkers />} />
              <Route path="/truth-markers/thanks" element={<TruthMarkers />} />
              <Route path="/marker-shop" element={<MarkerShop />} />
              <Route path="/m/:tm_id" element={<TruthMarkerPage />} />
              <Route path="/w/:id" element={<TruthWallPage />} />
              <Route path="/claim" element={<ClaimEntry />} />
              <Route path="/claim/:code" element={<ClaimTruth />} />
              <Route path="/truth-stand" element={<TruthStand />} />
              <Route path="/mirror-conversations" element={<MirrorConversations />} />
              <Route path="/reflection-library" element={<ReflectionLibrary />} />
              <Route path="/reflection-library/:slug" element={<ReflectionProductDetail />} />
              <Route path="/book" element={<BookingPage />} />
              <Route path="/client-portal" element={<ClientPortal />} />
              <Route path="/the-empty-chair" element={<EmptyChair />} />
              <Route path="/breathing-room" element={<BreathingRoom />} />
              <Route path="/workshops" element={<Workshops />} />
              <Route path="/workshops/:slug" element={<WorkshopDetail />} />
              <Route path="/guest-release" element={<GuestRelease />} />
              <Route path="/marker-collection" element={<MarkerCollection />} />
              <Route path="/marker-collection/gallery" element={<MCGallery />} />
              <Route path="/marker-collection/originals" element={<MCOriginals />} />
              <Route path="/marker-collection/commissions" element={<MCCommissions />} />
              <Route path="/marker-collection/about" element={<MCAbout />} />
              <Route path="/marker-collection/the-process" element={<MCProcess />} />
              <Route path="/marker-collection/booth" element={<BoothSalesMode />} />
              <Route path="/marker-collection/impact" element={<CharityImpactDashboard />} />
              <Route path="/marker-collection/challenge-board" element={<ChallengeBoard />} />
              <Route path="/marker-collection/:slug" element={<MarkerCollectionDetail />} />
              <Route path="/the-market" element={<TheMarket />} />
              <Route path="/the-market/:slug" element={<MarketProductDetail />} />
              <Route path="/name-winner" element={<NameWinner />} />
            </Routes>
          </BrowserRouter>
          <Toaster
            theme="dark"
            position="top-center"
            toastOptions={{
              style: {
                background: "#0a0a0a",
                color: "#F4F1EA",
                border: "1px solid rgba(201,160,85,0.4)",
                fontFamily: "Outfit, sans-serif",
                fontSize: "13px",
                letterSpacing: "0.04em",
                zIndex: 9999,
              },
            }}
          />
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
